import { Injectable, NgZone } from '@angular/core';         // Angular core: Injectable per i servizi, NgZone per far girare il codice nel contesto Angular
import { Events } from 'ionic-angular';                      // Sistema di eventi interno di Ionic (obsoleto nelle nuove versioni, ma in uso qui)
import { DomainEvents } from '../../models/models';          // Oggetto che contiene tutte le costanti di eventi usate nell'app

// Definizione dell'interfaccia del messaggio ricevuto da Android
export interface AndroidMessage {
	action: string;              // Tipo di azione richiesta (es: 'logout', 'voip-close', ecc.)
	message?: string;            // Messaggio opzionale, contenuto testuale o altro
	[key: string]: any;          // Altri campi generici opzionali
}

/**
 * Servizio Angular usato per gestire la comunicazione tra la WebApp (Ionic) e il contenitore nativo Android via WebView.
 *
 * Espone:
 * - una funzione `send_message()` per inviare messaggi dal frontend alla WebView Android
 * - un listener per intercettare i messaggi ricevuti dalla WebView e rilanciarli nel sistema eventi interno (`Events`)
 *
 * 📌 Questo file serve a centralizzare tutta la logica di comunicazione con Android, per evitare chiamate sparse e non manutenibili.
 *
	Come si usa?
	1. Per ricevere un messaggio da Android;
	this.events.subscribe(DomainEvents.ANDROID_MESSAGE_RECEIVED, (msg: AndroidMessage) => {console.log('Android ha inviato:', msg);});

	2. Per inviare un messaggio ad Android:
	this.androidBridge.send_message({action: 'impuls',message: 'ciao Android!'});
*/

@Injectable()
export class AndroidBridgeProvider {
	// Il costruttore inietta il sistema eventi (Events) e il contesto Angular (NgZone)
	constructor(
		private events: Events,
		private zone: NgZone
	) {
		// Subito alla creazione del servizio, attiva il listener dei messaggi in arrivo da Android
		this.init_message_listener();
	}

	/**
	 * Inizializza la funzione globale `fromAndroid()` che viene chiamata dalla WebView Android.
	 * Quando il contenitore chiama `window.fromAndroid(message)`, questo metodo:
	 * - prova a fare il parsing del messaggio JSON
	 * - lo rilancia come evento Ionic interno all'app
	 */
	private init_message_listener(): void {
		console.debug('[AndroidBridgeProvider.init_message_listener()] Inizializzo listener messaggi da Android')

		//singola inizializzazione garantita
		if (!(window as any).__is_android_listener_bound__) {
			(window as any).__is_android_listener_bound__ = true;
			//imposta/sostituisce la funzione che riceve i messaggi
			window['fromAndroid'] = (json: string) => {
				console.debug('[AndroidBridgeProvider.init_message_listener()/window.fromAndroid()] Ricevuto messaggio da Android:', json)
				try {
					const parsed: AndroidMessage = JSON.parse(json);  // Converte il messaggio da stringa a oggetto

					// Usa NgZone per far sì che il cambio di stato venga gestito correttamente da Angular
					this.zone.run(() => {
						console.group('[AndroidBridgeProvider.init_message_listener()/window.fromAndroid()] Propago messaggio parsato:', parsed)

						// Pubblica un evento globale che può essere intercettato da qualsiasi parte dell'app
						this.events.publish(DomainEvents.ANDROID_MESSAGE_RECEIVED, parsed);

						/**
						 * Se necessario, puoi anche rilanciare eventi più specifici in base al tipo di azione ricevuta.
						 * (commentato per ora — opzionale da attivare se serve)
						 *
						 * if (parsed.action === 'USER_LOGOUT_REQUESTED') {
						 *   this.events.publish(DomainEvents.USER_LOGOUT_REQUESTED, parsed);
						 * } else if (parsed.action === 'VOIPCALL_CLOSE') {
						 *   this.events.publish(DomainEvents.VOIPCALL_CLOSE, parsed);
						 * }
						 */
						console.groupEnd()
					});

				} catch (e) {
					// Se il JSON non è valido, stampa errore ma non blocca l'app
					console.error('[AndroidBridge] Invalid JSON message from Android:', json);
				}
			};
		}
	}

	/**
	 * Metodo pubblico per inviare un messaggio dal frontend al contenitore nativo Android.
	 * Verifica che una delle funzioni (`Android.send_message` o `sendToAndroid`) sia disponibile nella WebView.
	 *
	 * @param data Oggetto che contiene almeno un campo `action` (stringa) e opzionalmente altri dati
	 */
	public send(data: AndroidMessage): any {
		console.debug('[AndroidBridgeProvider.send()] Invio messaggio:', data)

		const json = JSON.stringify(data);  // Serializza il messaggio come stringa JSON
		let result = null
		// ✅ Caso 1: WebView ha iniettato `Android.sendMessage()` (nativo Android tipico)
		if ((window as any).Android && typeof (window as any).Android.sendMessage === 'function') {
			console.debug('[AndroidBridgeProvider.send()] Invio json tramite window.Android.send_message()', json)
			result = (window as any).Android.sendMessage(json);
			// ✅ Caso 2: È presente `sendToAndroid()` iniettata dalla WebView
		} else if ((window as any).Android && typeof (window as any).sendToAndroid === 'function') {
			console.debug('[AndroidBridgeProvider.send()] Invio json tramite window.sendToAndroid()', json)
			result = (window as any).sendToAndroid(json);
			// ❌ Nessuna funzione trovata → WebView non pronta o errore
		} else {
			console.warn('[AndroidBridgeProvider.send()] sendToAndroid not available');
		}
		console.debug('[AndroidBridgeProvider.send()] Esito richiesta:', result)
		return result
	}

	/**
	 * TODO agganciare alle UI
	 * @returns 
	 */
	public send_call(number: string, ringTimeout?: number, talkTimeout?: number): AndroidMessage {
		let result = this.send({ action: 'call', message: number, ringTimeout, talkTimeout })
		return result
	}
	/**
	 * TODO agganciare alle UI
	 * @returns 
	 */
	public send_close_app(): AndroidMessage {
		let result = this.send({ action: 'close_app', message: '' })
		return result
	}
	/**
	 * Usato internamente
	 * TODO siccome la risposta è asincrona, rendere sincrono o sostituire con ID generato + storage
	 * @returns 
	 */
	public send_get_id(): AndroidMessage {
		let result = this.send({ action: 'get_id', message: 'device' })
		return result
	}
	/**
	 * Invia comando 'impuls' con parametro stringa
	 * @param message
	 * @returns 
	 */
	public send_command_impuls(message: string): AndroidMessage {
		console.debug('[AndroidBridgeProvider.send_command_impuls()] Invio comando "inpuls"')
		let result = this.send({ action: 'impuls', message: message })
		return result
	}
	/**
	 * 
	 * @returns Usato internamente
	 */
	public send_off(): AndroidMessage {
		console.debug('[AndroidBridgeProvider.send_off()] Invio relè OFF')
		return this.send_command_impuls("\u00A0\u0001\u0000\u00A1")
	}
	/**
	 * Usato internamente
	 * @returns 
	 */
	public send_on(): AndroidMessage {
		console.debug('[AndroidBridgeProvider.send_on()] Invio relè ON')
		return this.send_command_impuls("\u00A0\u0001\u0001\u00A2")
	}

	private timeout_on: any = null;          // timer per i retry ON
	private timeout_off: any = null;         // timer per i retry OFF
	private timeout_after_on: any = null;     // delay 500ms tra ON e OFF

	public send_impulse(): Promise<AndroidMessage> {
		const max_attempts_on = 1;
		const max_attempts_off = 5;

		let is_promise_completed = false;

		const clear_timeout_on = () => { if (this.timeout_on) { clearTimeout(this.timeout_on); this.timeout_on = null; } };
		const clear_timeout_off = () => { if (this.timeout_off) { clearTimeout(this.timeout_off); this.timeout_off = null; } };
		const clear_timeout_after_on = () => { if (this.timeout_after_on) { clearTimeout(this.timeout_after_on); this.timeout_after_on = null; } };

		// stop cicli precedenti (pulisci tutti i timer noti)
		clear_timeout_on()
		clear_timeout_off()
		clear_timeout_after_on()

		return new Promise<AndroidMessage>((resolve, reject) => {
			let on_attempt = 1;
			let off_attempt = 1;

			const try_off = () => {
				if (is_promise_completed) return;
				if (off_attempt > max_attempts_off) {
					is_promise_completed = true;
					clear_timeout_on();
					clear_timeout_after_on();
					return reject(new Error(`OFF non confermato entro ${max_attempts_off} tentativi`));
				}
				console.debug(`[AndroidBridgeProvider.send_impulse] OFF tentativo ${off_attempt}/${max_attempts_off}`);
				try {
					const off_result = this.send_off();
					if (off_result && off_result.message === 'ok') {
						if (is_promise_completed) return;
						is_promise_completed = true;
						clear_timeout_on();
						clear_timeout_off();
						clear_timeout_after_on();
						console.debug('[AndroidBridgeProvider.send_impulse] OFF confermato');
						return resolve(off_result);
					}
				} catch (err) {
					console.debug('[AndroidBridgeProvider.send_impulse] OFF errore:', err);
				}
				off_attempt += 1;
				this.timeout_off = setTimeout(try_off, 1000);
			};

			const start_off_after_delay = () => {
				if (is_promise_completed) return;
				this.timeout_after_on = setTimeout(() => {
					clear_timeout_after_on();
					try_off();
				}, 500);
			};

			const try_on = () => {
				if (is_promise_completed) return;
				if (on_attempt > max_attempts_on) {
					console.debug(`[AndroidBridgeProvider.send_impulse] ON fallito dopo ${max_attempts_on} tentativi`);
					start_off_after_delay(); // OFF di sicurezza
					return;
				}
				console.debug(`[AndroidBridgeProvider.send_impulse] ON tentativo ${on_attempt}/${max_attempts_on}`);
				try {
					const on_result = this.send_on();
					if (on_result && on_result.message === 'ok') {
						console.debug('[AndroidBridgeProvider.send_impulse] ON confermato');
						start_off_after_delay();
						return;
					}
				} catch (err) {
					console.debug('[AndroidBridgeProvider.send_impulse] ON errore:', err);
				}
				on_attempt += 1;
				this.timeout_on = setTimeout(try_on, 1000);
			};

			try_on();
		});
	}
}




// WEBPACK FOOTER //
// ./src/providers/android/android-bridge.ts